/**
 * Hook for managing ticket inspections
 * 
 * Gets or creates an IN_PROGRESS inspection for a ticket using RPC
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface TicketInspection {
  id: string;
  shop_id: string;
  ticket_id: string;
  template_id: string;
  template_key: string;
  template_version: number;
  status: string;
  started_by: string | null;
  started_at: string | null;
  submitted_by: string | null;
  submitted_at: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

interface UseTicketInspectionResult {
  /** The inspection record */
  inspection: TicketInspection | null;
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Whether an inspection exists */
  hasInspection: boolean;
  /** Create or get the inspection */
  createInspection: () => Promise<TicketInspection | null>;
  /** Whether creation is in progress */
  isCreating: boolean;
  /** Refetch the inspection */
  refetch: () => void;
}

/**
 * Hook to get or create an inspection for a ticket
 * 
 * @param ticketId - The ticket ID
 * @param shopId - The shop ID
 * @param templateKey - The template key (default: 'DVI')
 */
export function useTicketInspection(
  ticketId: string | undefined,
  shopId: string | undefined,
  templateKey: string = 'DVI'
): UseTicketInspectionResult {
  const queryClient = useQueryClient();
  const queryKey = ['ticket-inspection', ticketId, templateKey];

  // Query to fetch existing inspection
  const {
    data: inspection,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!ticketId) return null;

      // First try to find existing inspection with this template key
      const { data, error } = await supabase
        .from('ticket_inspections')
        .select('*')
        .eq('ticket_id', ticketId)
        .eq('template_key', templateKey)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error fetching inspection:', error);
        throw error;
      }

      return data as TicketInspection | null;
    },
    enabled: !!ticketId,
  });

  // Mutation to create inspection via RPC
  const createMutation = useMutation({
    mutationFn: async () => {
      if (!ticketId || !shopId) {
        throw new Error('Ticket ID and Shop ID are required');
      }

      // Call the RPC to get or create inspection
      const { data, error } = await supabase.rpc('get_or_create_ticket_inspection', {
        p_ticket_id: ticketId,
        p_template_key: templateKey,
      });

      if (error) {
        console.error('Error creating inspection:', error);
        throw error;
      }

      // The RPC returns the inspection_id, fetch the full record
      const inspectionId = data;
      
      const { data: fullInspection, error: fetchError } = await supabase
        .from('ticket_inspections')
        .select('*')
        .eq('id', inspectionId)
        .single();

      if (fetchError) {
        throw fetchError;
      }

      return fullInspection as TicketInspection;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
    },
  });

  return {
    inspection: inspection ?? null,
    isLoading,
    error: error as Error | null,
    hasInspection: !!inspection,
    createInspection: async () => {
      const result = await createMutation.mutateAsync();
      return result;
    },
    isCreating: createMutation.isPending,
    refetch,
  };
}
